import { ChangeDetectorRef, ViewChild, Component, OnInit } from '@angular/core';
import { WidgetComponent } from '@dashboards/components/widget-component/widget-component';
import { Store } from '@ngrx/store';
import { of, forkJoin, Observable } from 'rxjs';
import { filter, take, map, tap } from 'rxjs';
import { ConfirmationService } from 'primeng/api';
import { InvoiceDocumentService } from '@invoice-upload/services/invoice-document.service';
import { InvoiceUpload } from '@invoice-upload/models/invoice-upload';
import { CreditorAccountService } from '@creditor-accounts/services/creditor-account.service';
import { SelectItem } from 'primeng/api';
import { VatRate } from '@feature/invoice-upload/models/vat-rate';
import { VatRateService } from '@invoice-upload/services/vat-rates.service';
import { ToastrService } from 'ngx-toastr';
import { Creditor } from '@feature/creditor-accounts/models/creditor-account-model';
import { UserSettingsService } from '@user-settings/services/user-settings.service';
import { InvoiceUploadWidgetConfigurationModel } from
  '@feature/invoice-upload/models/save-invoice-upload-widget-configuration-request';
import {MojoWellKnownNames} from "@feature/common";

@Component({
  selector: 'mojo-invoice-upload-widget',
  templateUrl: './invoice-upload-widget.component.html'
})
export class InvoiceUploadWidgetComponent
  extends WidgetComponent
  implements OnInit {

  @ViewChild('invoiceInputDialog')
  invoiceInputDialog;
  files: Observable<InvoiceUpload[]>;

  value = new Date();

  accounts: Observable<Creditor[]>;
  documentTypes: Observable<SelectItem[]>;
  vatRates: Observable<VatRate[]>;
  showAccounts: Observable<boolean>;
  formDesignId: Observable<number>;
  title: Observable<string>;
  creditNoteTransactionTypeCode: string;
  worksAddonEnabled: boolean;
  product: Observable<string>;

  constructor(
    private ref: ChangeDetectorRef,
    private readonly userSettings: UserSettingsService,
    private readonly vatRatesService: VatRateService,
    private readonly creditorAccountService: CreditorAccountService,
    private readonly invoiceDocumentService: InvoiceDocumentService,
    protected readonly store: Store<any>,
    protected confirmationService: ConfirmationService,
    private readonly messageService: ToastrService
  ) {
    super(store, confirmationService);
  }

  forceIERefresh() {
    const isIE11 = window.navigator.userAgent.toLowerCase().indexOf('trident') > -1;
    if (isIE11) {
      this.ref.detectChanges();
      console.log('IE 11 Refresh detect changes.');
    }

  }
  onInvoiceUploaded(uploadedInvoice: InvoiceUpload) {
    this.deleteFileAndLoadInvoices(uploadedInvoice).subscribe();
  }

  onFileUpload(files: File[]) {

    console.log('onFileUpload', files);

    this.invoiceDocumentService.uploadInvoicePdfs(files, this.widget.widgetConfigId).pipe(take(1), tap(() => {

      console.log('uploadInvoicePdfs', files);

      this.invoiceDocumentService.loadInvoices(this.widget.widgetConfigId);

      if (files && files.length === 1) {
        this.files.pipe(
          filter(fs => fs != undefined && fs.length > 0),
          tap(fs => {
            const filesOrderByDateAddedDesc = fs.sort((a, b) => new Date(b.DateAdded).getTime() - new Date(a.DateAdded).getTime());
            const singleFile = filesOrderByDateAddedDesc.find(f => f.FileName === files[0].name);
            if (singleFile) {
              this.onItemClick(singleFile);
            }
          }),
          take(1))
          .subscribe();
      }
    })).subscribe();

  }

  onItemClick(file: InvoiceUpload) {
    const blob = this.invoiceDocumentService.getInvoiceDocument(file.Id).pipe(take(1));

    blob.subscribe(blob => {
      this.invoiceInputDialog.showInvoiceInputDialog({
        invoiceUpload: file,
        invoicePdf: blob, creditNoteTransactionTypeCode: this.creditNoteTransactionTypeCode });
    });

  }

  deleteFileAndLoadInvoices(file: InvoiceUpload) {
    return this.invoiceDocumentService.deleteInvoice(file.Id).pipe(take(1),
      tap(() => {
        this.invoiceDocumentService.loadInvoices(this.widget.widgetConfigId);
      }));
  }

  onDeleteFile(file: InvoiceUpload) {
    this.deleteFileAndLoadInvoices(file).pipe(take(1), tap(() => {
      this.messageService.success(`File ${file.FileName} deleted.`);
    })).subscribe();
  }

  ngOnInit() {

    this.title = of(this.widget.title);

    this.files = this.invoiceDocumentService.getInvoices(this.widget.widgetConfigId).pipe(tap((f) => {
      console.log('files', f);
      this.forceIERefresh();
    }));

    const getWidgetConfig = this.invoiceDocumentService.getInvoiceUploadConfiguration(this.widget.widgetConfigId);
    const getDocTypes = this.invoiceDocumentService.getDocumentTypes();
    const getUserSettings = this.userSettings.getUserSettings().pipe(take(1));
    this.product = this.userSettings.getCurrentProduct();

    forkJoin({ config: getWidgetConfig, docTypes: getDocTypes, settings: getUserSettings }).pipe(
      take(1),
      tap((result) => {
        const findDocType = (code) => result.docTypes.find(dt => dt.Code === code);
        const config = result.config || {} as InvoiceUploadWidgetConfigurationModel;
        const invoiceDocType = findDocType(config.InvoiceTransactionTypeCode);
        const creditNoteDocType = findDocType(config.CreditNoteTransactionTypeCode);
        const previewMode = result.settings.IsInPreviewMode;

        const missingConfig = !invoiceDocType || !creditNoteDocType || !config.FormDesignId;
        if (missingConfig && previewMode) {
          this.messageService.error(
            '<p>This Widget has not been set up in the Mojo for Suppliers Widget Configuration.</p><p>Please provide these details before allowing Users to upload invoices to the application or contact your Mojo System Administrator.</p>',
            'Invoice Upload',
            { disableTimeOut: true, enableHtml: true });
        }

        const d = [invoiceDocType, creditNoteDocType].map(dt => {
          return {
            value: dt.Code,
            label: dt.Description
          }
        });
        this.documentTypes = of(d);
        this.formDesignId = of(config.FormDesignId);
        this.creditNoteTransactionTypeCode = config.CreditNoteTransactionTypeCode;
        this.worksAddonEnabled = result.settings.EnabledAddOns.includes(MojoWellKnownNames.AddOnCodes.WS007_Works);
      })).subscribe();

    this.accounts = this.creditorAccountService.getUserCreditors().pipe(map(accs => {
      return accs.map(acc => {
        acc.CreditorName = `${acc.AccountNo} - ${acc.CreditorName}`;
        return acc;
      });
    }));

    this.showAccounts = this.accounts.pipe(map(m => m.length > 1));

    this.vatRates = this.vatRatesService.getVatRates().pipe(map(vrs =>
      vrs.map(vr => {
        const percent = vr.VatPercent.toFixed(3);
        const newvr = { ...vr, ...{ Description: `${vr.Description} - (${percent}%)` } };
        return newvr as VatRate;
      })
    ));
    this.invoiceDocumentService.loadInvoices(this.widget.widgetConfigId);
  }

}
